import React, { useState } from "react";
import { FiltersButton, InfoBanner, Spinner, TableHeader, WithRoleAccess } from "@services/ui-components";
import { Col, Row } from "react-bootstrap";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { ApolloError, useReactiveVar } from "@apollo/client";
import { TechMapsFilterI } from "../types";
import { Link, useParams } from "react-router-dom";
import { useGetOneTechMapQuery } from "@me-team/host/main/technologicalMaps/graphql/technologicalMaps.hooks";
import ErrorService from "../../../services/ErrorService";
import TechMapServicesTable from "./TechMapServicesTable";
import TechMapServicesFilterWrapper from "./TechMapServicesFilterWrapper";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { CompanyAccess } from "@me-team/host/main/graphql/types";

const TechMapServices: React.FC = () => {
    const { t } = useTranslation();
    const { techMapId, branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const [showFilters, setShowFilters] = useState(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const { data: oneTechMapData, loading: oneTechMapLoading } = useGetOneTechMapQuery({
        variables: {
            techMapId: +techMapId,
            companyId: +currentCompanyId,
        },
        skip: !currentCompanyId,
        context: {
            errorType: "global",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const techMapName = oneTechMapData?.user?.company?.technologicalMaps?.technologicalMaps?.[0].name;

    const initialValueFilter: TechMapsFilterI = {
        search: null,
        categoryId: null,
    };

    const [filters, setFilters] = useState<TechMapsFilterI>(initialValueFilter);

    const toggleFilters = () => setShowFilters(!showFilters);

    if (oneTechMapLoading || !currentCompanyId) return <Spinner />;

    return (
        <div className="mb-9 px-2 px-lg-0">
            <InfoBanner>
                <p className="fs-7 mb-0">
                    {t(
                        "Select the services for which you want to use the technological card is to be used (the products from the technological card will be offered for write-off after the service is completed)."
                    )}
                </p>
            </InfoBanner>
            <TableHeader
                title={`${t("titleTechCartServices.Add Service to")} ${techMapName}`}
                paths={[
                    {
                        path: `/warehouse/technological-maps/${currentCompanyId}/${techMapId}/services/branch/${branchId}`,
                        label: t("Warehouse accounting"),
                    },
                    {
                        path: `/warehouse/technological-maps/${currentCompanyId}/branch/${branchId}`,
                        label: t("List of Technological Maps"),
                    },
                    {
                        path: `/warehouse/technological-maps/${currentCompanyId}/${techMapId}/services/branch/${branchId}`,
                        label: `${t("Add service to")} ${techMapName}`,
                    },
                ]}
                colSize={7}
                btnColSize={5}
                buttons={
                    <Row className="gx-3 mt-4 mt-lg-0 d-flex flex-lg-nowrap justify-content-end">
                        <WithRoleAccess permission={CompanyAccess.TechMapProductListView}>
                            <Col xs={12} md={6} className="mb-3 mb-md-0">
                                <Link
                                    to={`/warehouse/technological-maps/${currentCompanyId}/${techMapId}/products/branch/${branchId}`}
                                    className="btn btn-outline-orange px-2 w-100 text-truncate text-capitalize"
                                >
                                    {t("Products tech cards")}
                                </Link>
                            </Col>
                        </WithRoleAccess>
                        <Col xs={12} md={6} xxl={5}>
                            <FiltersButton
                                filter={filters}
                                showFilters={showFilters}
                                onClick={toggleFilters}
                                classes="mb-3 mb-lg-0"
                            />
                        </Col>
                    </Row>
                }
                showFilters={showFilters}
            >
                <TechMapServicesFilterWrapper setFilters={setFilters} />
            </TableHeader>
            <TechMapServicesTable filters={filters} />

            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </div>
    );
};

export default TechMapServices;
